import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Edit, ChevronRight, Plus, Mail, Phone, MapPin, Globe, Calendar, Award, X } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const SchoolProfile = () => {
  const [school, setSchool] = useState(null);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: 0,
    name: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    county: '',
    postal_code: '',
    extra_info: {
      established: '',
      motto: ''
    }
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('schoolprofile');

  useEffect(() => {
    const fetchSchoolData = async () => {
      try {
        setLoading(true);
        const schoolResponse = await api.get(`/api/v1/admin/schools/${user.school_reg}`);
        const classesResponse = await api.get(`/api/v1/admin/schools/${user.school_reg}/classes`);

        setSchool(schoolResponse.data);
        setClasses(classesResponse.data);
        setLoading(false);
      } catch (err) {
        setError(t('errors.loadFailed'));
        setLoading(false);
      }
    };

    fetchSchoolData();
  }, [api, user.school_reg, t]);

  // Update form data when school data is loaded
  useEffect(() => {
    if (school) {
      setFormData({
        id: school.id || 0,
        name: school.name || '',
        email: school.email || '',
        phone: school.phone || '',
        address: school.address || '',
        website: school.website || '',
        county: school.county || '',
        postal_code: school.postal_code || '',
        extra_info: {
          established: school.extra_info?.established || '',
          motto: school.extra_info?.motto || ''
        }
      });
    }
  }, [school]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const handleEditSchoolInfo = () => {
    setIsEditModalOpen(true);
  };

  const closeModal = () => {
    setIsEditModalOpen(false);
    setFormError(null);
  };

  const handleManageClasses = () => {
    navigate('/class-management');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'established' || name === 'motto') {
      setFormData({
        ...formData,
        extra_info: {
          ...formData.extra_info,
          [name]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);

    try {
      const response = await api.patch('/api/v1/admin/schools/profile', formData);
      // Update the school state with the new data
      setSchool(response.data);
      setIsEditModalOpen(false);
      toast.success(t('alerts.updateSuccess'), {
        position: 'top-center'
      });
    } catch (err) {
      setFormError(err.response?.data?.message || t('errors.updateFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-500 bg-red-50 p-4 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  const rtlClass = document.documentElement.dir === 'rtl' ? 'rtl text-right' : '';

  return (
  <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${rtlClass}`}>
    <Toaster
      position="top-center"
      toastOptions={{
        duration: 4000,
        style: {
          borderRadius: '10px',
          background: '#f8f9fa',
          color: '#0e7490',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          border: '1px solid #e2e8f0',
        },
      }}
    />

    {/* School Profile Section */}
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8">
      <div className="p-6 sm:p-8 border-b border-gray-100">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">{school.name}</h1>
          <button
            onClick={handleEditSchoolInfo}
            className="text-teal-600 hover:bg-teal-50 px-4 py-2 rounded-lg flex items-center transition-all duration-200 text-sm border border-teal-200"
          >
            <Edit className="h-4 w-4 mr-2" />
            {t('profile.editProfile')}
          </button>
        </div>
      </div>

      <div className="p-6 sm:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="flex items-start">
              <Mail className="h-5 w-5 text-teal-600 mt-1 mr-3" />
              <div>
                <p className="text-sm text-gray-500 mb-1">{t('profile.email')}</p>
                <p className="text-gray-800">{school.email}</p>
              </div>
            </div>

            <div className="flex items-start">
              <Phone className="h-5 w-5 text-teal-600 mt-1 mr-3" />
              <div>
                <p className="text-sm text-gray-500 mb-1">{t('profile.phone')}</p>
                <p className="text-gray-800">{school.phone}</p>
              </div>
            </div>

            <div className="flex items-start">
              <MapPin className="h-5 w-5 text-teal-600 mt-1 mr-3" />
              <div>
                <p className="text-sm text-gray-500 mb-1">{t('profile.location')}</p>
                <p className="text-gray-800">{school.address}, {school.county}, {school.postal_code}</p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex items-start">
              <Globe className="h-5 w-5 text-teal-600 mt-1 mr-3" />
              <div>
                <p className="text-sm text-gray-500 mb-1">{t('profile.website')}</p>
                <a href={school.website} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700">{school.website}</a>
              </div>
            </div>

            <div className="flex items-start">
              <Award className="h-5 w-5 text-teal-600 mt-1 mr-3" />
              <div>
                <p className="text-sm text-gray-500 mb-1">{t('profile.motto')}</p>
                <p className="text-gray-800 italic">"{school.extra_info.motto}"</p>
              </div>
            </div>

            <div className="flex items-start">
              <Calendar className="h-5 w-5 text-teal-600 mt-1 mr-3" />
              <div>
                <p className="text-sm text-gray-500 mb-1">{t('profile.established')}</p>
                <p className="text-gray-800">{school.extra_info.established}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Classes Section */}
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="p-6 sm:p-8 border-b border-gray-100">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-800">{t('classes.title')}</h2>
            <p className="text-gray-500 mt-1">{t('classes.totalClasses', { count: classes.length })}</p>
          </div>
          <button
            onClick={handleManageClasses}
            className="text-teal-600 hover:bg-teal-50 px-4 py-2 rounded-lg flex items-center transition-all duration-200 text-sm border border-teal-200"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('classes.manageClasses')}
          </button>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {classes.map((classItem) => (
            <div key={classItem.id} className="bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow transition-all duration-200 overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-800 flex items-center">
                    <BookOpen className="h-5 w-5 text-teal-600 mr-2" />
                    {classItem.name}
                  </h3>
                  <span className="bg-teal-50 text-teal-600 text-xs font-medium px-2.5 py-1 rounded-full">
                    {classItem.streams.length} {t('classes.streams')}
                  </span>
                </div>
              </div>

              <div className="p-4">
                <div className="space-y-2">
                  {classItem.streams.map((stream) => (
                    <div 
                      key={stream.id} 
                      className="flex justify-between items-center p-3 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <span className="text-gray-700">{stream.name}</span>
                      <button
                        onClick={() => window.location.href = `/admin/schools/${school.registration_number}/streams/${stream.id}`}
                        className="text-gray-400 hover:text-teal-600 p-1 rounded-full hover:bg-teal-50"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Edit School Modal */}
    {isEditModalOpen && (
      <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
        <div className={`bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-screen overflow-y-auto ${rtlClass}`}>
          <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800">{t('modal.editSchoolProfile')}</h2>
            <button
              onClick={closeModal}
              className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {formError && (
              <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg border border-red-100">
                {formError}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* School Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('modal.schoolName')}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('modal.email')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('modal.phone')}
                </label>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              {/* Website */}
              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('modal.website')}
                </label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              {/* Address */}
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('modal.address')}
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              {/* County */}
              <div>
                <label htmlFor="county" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('modal.county')}
                </label>
                <input
                  type="text"
                  id="county"
                  name="county"
                  value={formData.county}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              {/* Postal Code */}
              <div>
                <label htmlFor="postal_code" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('modal.postalCode')}
                </label>
                <input
                  type="text"
                  id="postal_code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              {/* Established */}
              <div>
                <label htmlFor="established" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('modal.establishedYear')}
                </label>
                <input
                  type="text"
                  id="established"
                  name="established"
                  value={formData.extra_info.established}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              {/* Motto */}
              <div className="md:col-span-2">
                <label htmlFor="motto" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('modal.schoolMotto')}
                </label>
                <input
                  type="text"
                  id="motto"
                  name="motto"
                  value={formData.extra_info.motto}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="mt-8 flex justify-end space-x-4">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
              >
                {t('modal.cancel')}
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitting ? t('modal.saving') : t('modal.saveChanges')}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
  </div>
);
};

export default SchoolProfile;